import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileDown, Send, CheckCircle, AlertTriangle, Trash2, Loader2, Mail, BellRing, Pencil, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatCurrency, formatDate, CURRENCY_LIST } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_ITEM = { description: '', hsn_sac_code: '', quantity: 1, unit_price: 0, tax_type: 'none', custom_tax_name: '', tax_rate: 0, cgst_rate: 0, sgst_rate: 0, igst_rate: 0, cgst_amount: 0, sgst_amount: 0, igst_amount: 0, tax_amount: 0, amount: 0 };
const EMPTY_FORM = { client_id: '', client_name: '', client_email: '', billing_address: '', custom_invoice_number: '', po_number: '', invoice_date: new Date().toISOString().split('T')[0], credit_terms: 0, amount: 0, subtotal: 0, tax_amount: 0, currency: 'USD', description: '', due_date: '', bank_details: '', notes: '', items: [{ ...EMPTY_ITEM }] };
const CREDIT_TERMS = [0, 15, 30, 45, 60, 90];
const TAX_TYPES = [
  { value: 'none', label: 'No Tax' },
  { value: 'igst', label: 'IGST' },
  { value: 'cgst_sgst', label: 'CGST + SGST' },
  { value: 'custom', label: 'Custom Tax' },
];

const EMPTY_EMAIL = { to_email: '', subject: '', message: '', email_type: 'invoice' };
const AR_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
];

export default function AccountsReceivablePage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ ...EMPTY_EMAIL });
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewNumber, setPreviewNumber] = useState('');
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [confirmSendInvoice, setConfirmSendInvoice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchInvoices = useCallback(async () => {
    try { const { data } = await api.get('/invoices/receivable'); setInvoices(data); } catch { toast.error('Failed to load invoices'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const filteredInvoices = useMemo(() => {
    let list = invoices;
    if (statusFilter !== 'all') {
      list = list.filter(inv => inv.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(q) ||
        (inv.client_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [invoices, searchQuery, statusFilter]);

  const fetchClients = useCallback(async () => {
    try { const { data } = await api.get('/clients'); setClients(data); } catch {}
  }, []);

  const openCreateDialog = async () => {
    setEditId(null);
    const newForm = { ...EMPTY_FORM };
    fetchClients();
    try {
      const { data } = await api.get('/company-settings');
      if (data) {
        const bankParts = [data.bank_name, data.bank_account ? `A/C: ${data.bank_account}` : '', data.bank_ifsc ? `IFSC: ${data.bank_ifsc}` : '', data.bank_branch ? `Branch: ${data.bank_branch}` : ''].filter(Boolean);
        if (bankParts.length) newForm.bank_details = bankParts.join('\n');
      }
    } catch {}
    setForm(newForm);
    setDialogOpen(true);
  };

  const openEditDialog = async (inv) => {
    setEditId(inv.id);
    fetchClients();
    setForm({
      client_id: inv.client_id || '', client_name: inv.client_name || '', client_email: inv.client_email || '',
      billing_address: inv.billing_address || '', custom_invoice_number: inv.invoice_number || '', po_number: inv.po_number || '',
      invoice_date: inv.invoice_date || '',
      credit_terms: inv.credit_terms || 0, amount: inv.amount || 0, subtotal: inv.subtotal || 0,
      tax_amount: inv.tax_amount || 0, currency: inv.currency || 'USD', description: inv.description || '',
      due_date: inv.due_date || '', bank_details: inv.bank_details || '', notes: inv.notes || '',
      items: (inv.items && inv.items.length > 0) ? inv.items : [{ ...EMPTY_ITEM }]
    });
    setDialogOpen(true);
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(x => x.id === clientId);
    if (c) {
      const addr = [c.address, c.city, c.state, c.country].filter(Boolean).join(', ');
      setForm({ ...form, client_id: clientId, client_name: c.name, client_email: c.email || '', billing_address: addr });
    }
  };

  const recalcTotals = (items) => {
    const subtotal = items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unit_price)), 0);
    const taxTotal = items.reduce((s, i) => s + Number(i.tax_amount), 0);
    return { subtotal, tax_amount: taxTotal, amount: subtotal + taxTotal };
  };

  const calcItemTax = (item) => {
    const lineTotal = Number(item.quantity) * Number(item.unit_price);
    const rate = Number(item.tax_rate);
    let cgst_rate = 0, sgst_rate = 0, igst_rate = 0, cgst_amount = 0, sgst_amount = 0, igst_amount = 0, tax_amount = 0;
    if (item.tax_type === 'igst') {
      igst_rate = rate;
      igst_amount = lineTotal * (rate / 100);
      tax_amount = igst_amount;
    } else if (item.tax_type === 'cgst_sgst') {
      cgst_rate = rate / 2;
      sgst_rate = rate / 2;
      cgst_amount = lineTotal * (cgst_rate / 100);
      sgst_amount = lineTotal * (sgst_rate / 100);
      tax_amount = cgst_amount + sgst_amount;
    } else if (item.tax_type === 'custom') {
      tax_amount = lineTotal * (rate / 100);
    }
    return { ...item, cgst_rate, sgst_rate, igst_rate, cgst_amount, sgst_amount, igst_amount, tax_amount, amount: lineTotal + tax_amount };
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'tax_type') {
      items[idx].tax_rate = items[idx].tax_type === 'none' ? 0 : items[idx].tax_rate;
    }
    if (['quantity', 'unit_price', 'tax_rate', 'tax_type'].includes(field)) {
      items[idx] = calcItemTax(items[idx]);
    }
    const totals = recalcTotals(items);
    setForm({ ...form, items, ...totals });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] });
  const removeItem = (idx) => {
    const items = form.items.filter((_, i) => i !== idx);
    const totals = recalcTotals(items);
    setForm({ ...form, items, ...totals });
  };

  const handleCreditTerms = (days) => {
    const d = Number(days);
    let due = form.due_date;
    if (d > 0 && form.invoice_date) {
      const inv = new Date(form.invoice_date);
      inv.setDate(inv.getDate() + d);
      due = inv.toISOString().split('T')[0];
    }
    setForm({ ...form, credit_terms: d, due_date: due });
  };

  const handleSave = async () => {
    if (!form.client_id) { toast.error('Please select a registered client'); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/invoices/receivable/${editId}`, form);
        toast.success('Invoice updated');
      } else {
        await api.post('/invoices/receivable', form);
        toast.success('Invoice created');
      }
      setDialogOpen(false); setEditId(null); setForm({ ...EMPTY_FORM }); fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save invoice'); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/invoices/receivable/${id}/status`, { status }); toast.success(`Invoice marked as ${status}`); fetchInvoices(); }
    catch { toast.error('Failed to update status'); }
  };

  const handleMarkAsSent = (inv) => {
    setConfirmSendInvoice(inv);
    setConfirmSendOpen(true);
  };

  const confirmMarkAsSent = async (alsoEmail) => {
    if (!confirmSendInvoice) return;
    await updateStatus(confirmSendInvoice.id, 'sent');
    if (alsoEmail) {
      openEmailDialog(confirmSendInvoice, 'invoice');
    }
    setConfirmSendOpen(false);
    setConfirmSendInvoice(null);
  };

  const deleteInvoice = async (id) => {
    try { await api.delete(`/invoices/receivable/${id}`); toast.success('Invoice deleted'); fetchInvoices(); }
    catch { toast.error('Failed to delete'); }
  };

  const openEmailDialog = (inv, type = 'invoice') => {
    setEmailInvoice(inv);
    setEmailForm({
      to_email: inv.client_email || '',
      subject: type === 'reminder'
        ? `Payment Reminder: Invoice ${inv.invoice_number}`
        : `Invoice ${inv.invoice_number} from GO MO Group DBS`,
      message: '',
      email_type: type
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.to_email) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      await api.post(`/invoices/receivable/${emailInvoice.id}/send-email`, emailForm);
      toast.success(`Email sent to ${emailForm.to_email}`);
      setEmailDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
    setSending(false);
  };

  const downloadPdf = async (id, number) => {
    try {
      const resp = await fetch(`${API_URL}/api/invoices/receivable/${id}/pdf`, { credentials: 'include' });
      const blob = await resp.blob(); const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${number}.pdf`; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const openPreview = async (id, number) => {
    try {
      const resp = await fetch(`${API_URL}/api/invoices/receivable/${id}/pdf`, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewNumber(number);
      setPreviewOpen(true);
    } catch { toast.error('Failed to load preview'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="accounts-receivable-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Accounts Receivable</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage client invoices issued</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="create-receivable-button">
          <Plus size={16} /> New Invoice
        </Button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3" data-testid="ar-filter-bar">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by invoice # or client..."
            className="pl-9 bg-[hsl(var(--surface-input))] border-input text-foreground h-9"
            data-testid="ar-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-[hsl(var(--surface-input))] border-input text-foreground h-9" data-testid="ar-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {AR_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value} className="text-foreground">{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(searchQuery || statusFilter !== 'all') && (
          <span className="text-xs text-muted-foreground" data-testid="ar-result-count">{filteredInvoices.length} result{filteredInvoices.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Invoice #</TableHead>
                <TableHead className="text-muted-foreground">Client</TableHead>
                <TableHead className="text-muted-foreground">Amount</TableHead>
                <TableHead className="text-muted-foreground">Due Date</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground py-8"><Loader2 className="animate-spin inline" size={16} /> Loading...</TableCell></TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{invoices.length === 0 ? 'No client invoices yet. Create one to get started.' : 'No invoices match your search.'}</TableCell></TableRow>
              ) : filteredInvoices.map(inv => (
                <TableRow key={inv.id} className="border-border hover:bg-muted/50" data-testid={`receivable-row-${inv.id}`}>
                  <TableCell className="text-card-foreground/80 font-mono text-xs">{inv.invoice_number}</TableCell>
                  <TableCell className="text-foreground">{inv.client_name}</TableCell>
                  <TableCell className="text-foreground font-mono">{formatCurrency(inv.amount, inv.currency)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(inv.due_date)}</TableCell>
                  <TableCell><Badge variant="outline" className={`status-${inv.status} text-xs capitalize`}>{inv.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => openEditDialog(inv)} data-testid={`edit-btn-ar-${inv.id}`} title="Edit Invoice"><Pencil size={14} /></Button>
                      {inv.status === 'draft' && (
                        <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => handleMarkAsSent(inv)} data-testid={`send-btn-${inv.id}`} title="Mark as Sent"><Send size={14} /></Button>
                      )}
                      {inv.status === 'sent' && (
                        <>
                          <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-7 px-2" onClick={() => updateStatus(inv.id, 'paid')} data-testid={`paid-btn-${inv.id}`} title="Mark as Paid"><CheckCircle size={14} /></Button>
                          <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300 h-7 px-2" onClick={() => updateStatus(inv.id, 'overdue')} data-testid={`overdue-btn-${inv.id}`} title="Mark as Overdue"><AlertTriangle size={14} /></Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" className="text-violet-400 hover:text-violet-300 h-7 px-2" onClick={() => openEmailDialog(inv, 'invoice')} data-testid={`email-btn-${inv.id}`} title="Send Invoice Email"><Mail size={14} /></Button>
                      {(inv.status === 'sent' || inv.status === 'overdue') && (
                        <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300 h-7 px-2" onClick={() => openEmailDialog(inv, 'reminder')} data-testid={`reminder-btn-${inv.id}`} title="Send Payment Reminder"><BellRing size={14} /></Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-cyan-400 hover:text-cyan-300 h-7 px-2" onClick={() => openPreview(inv.id, inv.invoice_number)} data-testid={`preview-btn-ar-${inv.id}`} title="Preview Invoice"><Eye size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`pdf-btn-ar-${inv.id}`} title="Download PDF"><FileDown size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-400 h-7 px-2" onClick={() => deleteInvoice(inv.id)} data-testid={`delete-btn-ar-${inv.id}`} title="Delete"><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="create-receivable-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground">{editId ? 'Edit Client Invoice' : 'New Client Invoice'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{editId ? 'Update the invoice details' : 'Create an invoice to send to a client'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Client Selection */}
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Select Client *</Label>
              {clients.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2" data-testid="no-clients-msg">
                  No clients registered. Please add clients in <a href="/contacts" className="text-blue-500 underline">Clients & Vendors</a> first.
                </div>
              ) : (
                <Select value={form.client_id} onValueChange={handleClientSelect}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-client-select">
                    <SelectValue placeholder="Choose a registered client" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-foreground">
                        <span className="font-medium">{c.name}</span>
                        {c.email && <span className="text-muted-foreground ml-2 text-xs">({c.email})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {form.client_id && (
              <div className="grid grid-cols-2 gap-4 p-3 border border-border rounded-md bg-muted/30">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Client</p>
                  <p className="text-foreground text-sm font-medium mt-0.5">{form.client_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Email</p>
                  <p className="text-foreground text-sm mt-0.5">{form.client_email || '-'}</p>
                </div>
                {form.billing_address && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Address</p>
                    <p className="text-foreground text-sm mt-0.5">{form.billing_address}</p>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Invoice Number</Label>
                <Input value={form.custom_invoice_number} onChange={e => setForm({ ...form, custom_invoice_number: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-custom-invoice-number"
                  placeholder={editId ? '' : 'Auto-generated if left blank'} />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">PO Number</Label>
                <Input value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-po-number"
                  placeholder="Client's PO reference" />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Currency</Label>
                <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {CURRENCY_LIST.map(c => <SelectItem key={c} value={c} className="text-foreground">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Invoice Date</Label>
                <Input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-invoice-date-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Credit Terms</Label>
                <Select value={String(form.credit_terms)} onValueChange={handleCreditTerms}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-credit-terms-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {CREDIT_TERMS.map(d => <SelectItem key={d} value={String(d)} className="text-foreground">{d === 0 ? 'Custom' : `Net ${d} days`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-due-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="ar-description-input" placeholder="Invoice description" />
              </div>
            </div>
            {/* Line Items with HSN/SAC and GST Tax */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-card-foreground/80 text-sm font-medium">Line Items</Label>
                <Button size="sm" variant="outline" onClick={addItem} className="border-input text-card-foreground/80 h-7 text-xs" data-testid="ar-add-item-button"><Plus size={12} className="mr-1" /> Add Item</Button>
              </div>
              {/* Table Header */}
              <div className="grid grid-cols-24 gap-2 px-1">
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">HSN/SAC</span></div>
                <div className="col-span-5"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Description</span></div>
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Qty</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Rate</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax Type</span></div>
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax %</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax Amt</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Amount</span></div>
                <div className="col-span-1"></div>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-24 gap-2 items-center">
                    <div className="col-span-2">
                      <Input value={item.hsn_sac_code} onChange={e => updateItem(idx, 'hsn_sac_code', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="Code" data-testid={`ar-item-hsn-${idx}`} />
                    </div>
                    <div className="col-span-5">
                      <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="Item description" data-testid={`ar-item-desc-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm text-center" data-testid={`ar-item-qty-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="0.00" data-testid={`ar-item-price-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Select value={item.tax_type} onValueChange={v => updateItem(idx, 'tax_type', v)}>
                        <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-xs" data-testid={`ar-item-taxtype-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          {TAX_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-foreground text-xs">{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.tax_rate} onChange={e => updateItem(idx, 'tax_rate', e.target.value)}
                        className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm text-center" placeholder="0"
                        disabled={item.tax_type === 'none'} data-testid={`ar-item-tax-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <div className="h-9 flex items-center text-sm text-muted-foreground font-mono px-2 bg-muted/30 rounded-md border border-transparent">{formatCurrency(item.tax_amount, form.currency)}</div>
                    </div>
                    <div className="col-span-3">
                      <div className="h-9 flex items-center text-sm text-foreground font-mono font-medium px-2 bg-muted/30 rounded-md border border-transparent">{formatCurrency(item.amount, form.currency)}</div>
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {form.items.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 h-8 w-8 p-0" onClick={() => removeItem(idx)} title="Remove Item"><Trash2 size={14} /></Button>}
                    </div>
                    {/* CGST/SGST breakdown inline */}
                    {item.tax_type === 'cgst_sgst' && item.tax_rate > 0 && (
                      <div className="col-span-24 -mt-1 mb-1 pl-7">
                        <p className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
                          CGST @ {item.cgst_rate}%: {formatCurrency(item.cgst_amount, form.currency)} &nbsp;&bull;&nbsp; SGST @ {item.sgst_rate}%: {formatCurrency(item.sgst_amount, form.currency)}
                        </p>
                      </div>
                    )}
                    {/* Custom tax name input */}
                    {item.tax_type === 'custom' && (
                      <div className="col-span-24 -mt-1 mb-1 pl-7 flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">Tax name:</span>
                        <input value={item.custom_tax_name || ''} onChange={e => updateItem(idx, 'custom_tax_name', e.target.value)}
                          className="bg-[hsl(var(--surface-input))] border border-input text-foreground rounded px-2 py-0.5 text-xs w-40 focus:border-blue-500 outline-none"
                          placeholder="e.g. VAT, Service Tax, Cess" data-testid={`ar-item-customtax-${idx}`} />
                        {item.tax_rate > 0 && <span className="text-[11px] text-muted-foreground font-mono">{item.custom_tax_name || 'Tax'} @ {item.tax_rate}%: {formatCurrency(item.tax_amount, form.currency)}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 pt-3 border-t border-border">
              <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Subtotal: {formatCurrency(form.subtotal, form.currency)}</div>
              {(() => {
                const totalCgst = form.items.reduce((s, i) => s + (i.cgst_amount || 0), 0);
                const totalSgst = form.items.reduce((s, i) => s + (i.sgst_amount || 0), 0);
                const totalIgst = form.items.reduce((s, i) => s + (i.igst_amount || 0), 0);
                // Group custom taxes by name
                const customTaxes = {};
                form.items.filter(i => i.tax_type === 'custom' && i.tax_amount > 0).forEach(i => {
                  const name = i.custom_tax_name || 'Tax';
                  customTaxes[name] = (customTaxes[name] || 0) + i.tax_amount;
                });
                return (
                  <>
                    {totalCgst > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>CGST: {formatCurrency(totalCgst, form.currency)}</div>}
                    {totalSgst > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>SGST: {formatCurrency(totalSgst, form.currency)}</div>}
                    {totalIgst > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>IGST: {formatCurrency(totalIgst, form.currency)}</div>}
                    {Object.entries(customTaxes).map(([name, amt]) => (
                      <div key={name} className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{name}: {formatCurrency(amt, form.currency)}</div>
                    ))}
                    {!totalCgst && !totalSgst && !totalIgst && !Object.keys(customTaxes).length && form.tax_amount > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Tax: {formatCurrency(form.tax_amount, form.currency)}</div>}
                  </>
                );
              })()}
              <div className="text-lg font-bold text-card-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Total: {formatCurrency(form.amount, form.currency)}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Bank Details</Label>
                <textarea value={form.bank_details} onChange={e => setForm({ ...form, bank_details: e.target.value })}
                  className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[60px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  data-testid="ar-bank-details-input" placeholder="Bank name, account number, IFSC..." />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Notes</Label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[60px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  data-testid="ar-notes-input" placeholder="Additional notes for the invoice" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-input text-card-foreground/80">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-receivable-button">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editId ? 'Update Invoice' : 'Create Invoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Send Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground flex items-center gap-2">
              {emailForm.email_type === 'reminder' ? <><BellRing size={18} className="text-amber-400" /> Send Payment Reminder</> : <><Mail size={18} className="text-violet-400" /> Send Invoice Email</>}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {emailForm.email_type === 'reminder'
                ? `Send a payment reminder for ${emailInvoice?.invoice_number}`
                : `Send invoice ${emailInvoice?.invoice_number} with PDF attachment`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Recipient Email *</Label>
              <Input value={emailForm.to_email} onChange={e => setEmailForm({ ...emailForm, to_email: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="email-to-input" placeholder="client@company.com" />
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Subject</Label>
              <Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="email-subject-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Custom Message (optional)</Label>
              <textarea value={emailForm.message} onChange={e => setEmailForm({ ...emailForm, message: e.target.value })}
                className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[80px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                data-testid="email-message-input" placeholder="Add a personal message..." />
            </div>
            {emailForm.email_type !== 'reminder' && (
              <div className="bg-[hsl(var(--surface-input))] border border-border rounded-md p-3 text-xs text-muted-foreground">
                <Mail size={12} className="inline mr-1" /> Invoice PDF will be attached automatically
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} className="border-input text-card-foreground/80">Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sending}
              className={emailForm.email_type === 'reminder' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-violet-600 hover:bg-violet-700 text-white'}
              data-testid="confirm-send-email-button">
              {sending ? <Loader2 className="animate-spin" size={16} /> : (emailForm.email_type === 'reminder' ? 'Send Reminder' : 'Send Email')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={(open) => { if (!open && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } setPreviewOpen(open); }}>
        <DialogContent className="max-w-4xl bg-card border-border h-[90vh] flex flex-col" data-testid="preview-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-card-foreground flex items-center gap-2"><Eye size={18} /> Invoice Preview — {previewNumber}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Preview the invoice before downloading or sending</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 rounded-md overflow-hidden border border-border bg-white">
            {previewUrl && (
              <iframe src={previewUrl + "#toolbar=1&navpanes=0"} className="w-full h-full" title="PDF Preview" style={{border:"none"}} />
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-input text-card-foreground/80">Close</Button>
            <Button onClick={() => { if (previewUrl) { const a = document.createElement('a'); a.href = previewUrl; a.download = `${previewNumber}.pdf`; a.click(); } }} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="preview-download-button">
              <FileDown size={14} /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Mark as Sent Dialog */}
      <Dialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="confirm-send-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground flex items-center gap-2"><Send size={18} className="text-blue-400" /> Mark as Sent</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Would you also like to email this invoice to <span className="text-foreground font-medium">{confirmSendInvoice?.client_email || 'the client'}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setConfirmSendOpen(false); setConfirmSendInvoice(null); }} className="border-input text-card-foreground/80" data-testid="confirm-send-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => confirmMarkAsSent(false)} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid="confirm-send-only">
              <Send size={14} className="mr-2" /> Mark as Sent Only
            </Button>
            <Button onClick={() => confirmMarkAsSent(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="confirm-send-and-email">
              <Mail size={14} className="mr-2" /> Mark as Sent & Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
